import { Collapse } from '@mui/material'
import clsx from 'clsx'
import Icon from 'components/core-v2/atoms/Icon'
import { CheckboxField } from 'components/core-v2/molecules/fields/checkbox-field'
import {
  TRI_STATE_CHECKBOX,
  TriStateCheckboxField
} from 'components/core-v2/molecules/fields/tri-state-checkbox-field'
import { FC, useCallback, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { useDeepEffect } from '../../../../utilities/hooks/useDeepEffect'
import { useIsFirstMount } from '../../../../utilities/hooks/useIsFirstMount'
import { doesIncludeCaseInsensitive } from '../../../../utilities/regex'
import { BRANCH_TYPE } from './data'
import { NoResultsFound } from './no-results-found'
import classes from './tree.module.scss'
import { useData, useSearchValue } from './utils'

const HighlightedLabel = ({ label }: { label: string }) => {
  const { isSearchActive, searchValue } = useSearchValue()

  if (!isSearchActive) return <span>{label}</span>

  const split = label.split(new RegExp(`(${searchValue})`, 'ig'))

  return (
    <span className={classes.highlightedLabel}>
      {split.map(string =>
        doesIncludeCaseInsensitive(string, searchValue) ? (
          <strong key={string}>{string}</strong>
        ) : (
          string
        )
      )}
    </span>
  )
}

export interface BranchData {
  amount: number
  childIds?: string[]
  childIdsString?: string
  childMatchesSearch?: boolean
  id: string
  isExpandable?: boolean
  label: string
  subBranch?: BranchData[]
  type: BRANCH_TYPE
}

interface BranchProps extends BranchData {
  level: number
}

const Branch: FC<BranchProps> = ({
  amount,
  childIds,
  childIdsString,
  childMatchesSearch,
  children,
  id,
  isExpandable = true,
  label,
  level,
  subBranch,
  type
}) => {
  const { isSearchActive, searchValue } = useSearchValue()
  const [isOpen, setIsOpen] = useState(false)
  const { setValue } = useFormContext()
  const directChildValues = useWatch({ name: childIds })
  const selfValue = useWatch({ name: id })
  const isFirstMount = useIsFirstMount()

  // Open or close the branch based on the search
  useDeepEffect(() => {
    if (childMatchesSearch && isSearchActive) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [childMatchesSearch, isSearchActive, label, searchValue])

  // Set the tri state checkboxes to false/mixed/true when their children change
  useDeepEffect(() => {
    if (!subBranch || !directChildValues || directChildValues.length === 0 || isFirstMount) return

    const areAllChildrenTrue = directChildValues.every(
      value => value === true || value === TRI_STATE_CHECKBOX.true
    )
    const areAllChildrenFalse = directChildValues.every(
      value => value === false || value === TRI_STATE_CHECKBOX.false || !value
    )

    if (subBranch && areAllChildrenTrue) {
      setValue(id, TRI_STATE_CHECKBOX.true)
    } else if (subBranch && areAllChildrenFalse) {
      setValue(id, TRI_STATE_CHECKBOX.false)
    } else if (subBranch && !areAllChildrenFalse && !areAllChildrenTrue) {
      setValue(id, TRI_STATE_CHECKBOX.mixed)
    }
  }, [directChildValues])

  const toggleIsOpen = useCallback(() => setIsOpen(isOpen => !isOpen), [])

  const triStateOnClick = useCallback(
    newValue => {
      if (newValue === TRI_STATE_CHECKBOX.true) {
        if (type === BRANCH_TYPE.client) {
          childIds.forEach(id => setValue(id, TRI_STATE_CHECKBOX.true))
          subBranch.forEach(branch => {
            branch.subBranch.forEach(branch => setValue(branch.id, true))
          })
        } else {
          childIds.forEach(id => setValue(id, true))
        }
      }

      if (newValue === TRI_STATE_CHECKBOX.false) {
        if (type === BRANCH_TYPE.client) {
          childIds.forEach(id => setValue(id, TRI_STATE_CHECKBOX.false))
          subBranch.forEach(branch => {
            branch.subBranch.forEach(branch => setValue(branch.id, false))
          })
        } else {
          childIds.forEach(id => setValue(id, false))
        }
      }
    },
    [childIds, setValue, subBranch, type]
  )

  const isTopLevelActive =
    level === 1 && (selfValue === TRI_STATE_CHECKBOX.mixed || selfValue === TRI_STATE_CHECKBOX.true)

  return (
    <div
      aria-expanded={isExpandable ? isOpen : undefined}
      aria-selected={isTopLevelActive}
      className={clsx(classes.branch, {
        [classes.branchOpen]: isOpen,
        [classes.branchIsExpandable]: isExpandable,
        [classes.topLevelActive]: isTopLevelActive
      })}
      role="treeitem"
    >
      <div className={clsx(classes.row, { [classes.rowOpen]: isOpen })}>
        {isExpandable && (
          <button
            className={clsx(classes.chevronButton, { [classes.chevronButtonOpen]: isOpen })}
            type="button"
            onClick={toggleIsOpen}
          >
            <Icon name="chevronDown" />
          </button>
        )}
        {isExpandable ? (
          <TriStateCheckboxField
            ariaControls={childIdsString}
            className={clsx(classes.checkbox, { [classes.topLevelActive]: isTopLevelActive })}
            id={id}
            label={<HighlightedLabel label={label} />}
            onClick={triStateOnClick}
          />
        ) : (
          <CheckboxField
            className={classes.checkbox}
            id={id}
            label={<HighlightedLabel label={label} />}
          />
        )}

        <span className={classes.amount}>{amount}</span>
      </div>

      {isExpandable && (
        <Collapse in={isOpen}>
          <div role="group">{children}</div>
        </Collapse>
      )}
    </div>
  )
}

const RecursiveBranch = ({ branch, level }: { branch: BranchData; level: number }) => (
  <Branch {...branch} isExpandable={Boolean(branch.subBranch)} level={level}>
    {branch.subBranch?.map(branch => (
      <RecursiveBranch key={branch.id} branch={branch} level={level + 1} />
    ))}
  </Branch>
)

export const Tree = () => {
  const { data } = useData()

  return (
    <div aria-multiselectable data-private className={classes.tree} role="tree">
      {data.map(branch => (
        <RecursiveBranch key={branch.id} branch={branch} level={1} />
      ))}
      {data.length === 0 && <NoResultsFound />}
    </div>
  )
}
